import React, { useRef, useEffect } from 'react';
import { Text } from '@/ui-kits';
import { StepProps } from './types';
import { Routes } from '@/navigation/Routes';
import { resetPatientDashboardState, setCurrentConsultationTab } from '@/services/redux/slices';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ConsultationGroup } from '@/constants/enums';
import { videos } from '@/constants';

export const SecondOpinion: React.FC<StepProps> = ({ open, success }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!videoRef.current || !containerRef.current) return;

    const video = videoRef.current;
    video.playbackRate = 1;

    let isInViewport = true;

    // Keep video playing even when out of viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isInViewport = entry.isIntersecting;
          if (!entry.isIntersecting) {
            // Video is out of viewport - ensure it keeps playing
            if (video.paused && video.currentTime < video.duration) {
              video.play().catch((error) => {
                console.error('Error playing video:', error);
              });
            }
          }
        });
      },
      {
        threshold: 0, // Trigger even when 0% visible
      }
    );

    observer.observe(containerRef.current);

    // Handle video pause events (browser may pause when out of viewport)
    const handlePause = () => {
      // Only resume if video is out of viewport and not at the end
      // This prevents browser from pausing the video when scrolling away
      if (!isInViewport && video.currentTime < video.duration) {
        // Use a small delay to avoid conflicts with browser's pause behavior
        setTimeout(() => {
          if (video.paused && !isInViewport && video.currentTime < video.duration) {
            video.play().catch((error) => {
              console.error('Error resuming video:', error);
            });
          }
        }, 100);
      }
    };

    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', () => {
      if (open) {
        dispatch(setCurrentConsultationTab(ConsultationGroup.Ongoing));
        navigate(Routes.CONSULTATION, {
          state: { tab: ConsultationGroup.Ongoing },
        });
        dispatch(resetPatientDashboardState());
      }
    });

    // Cleanup
    return () => {
      observer.disconnect();
      video.removeEventListener('pause', handlePause);
    };
  }, [open, dispatch, navigate]);

  return (
    <div
      ref={containerRef}
      style={{ boxShadow: '0px 4px 41px -9px #00000033' }}
      className="patient-dashboard-accordion-layout-container bg-white rounded-2xl overflow-hidden p-4 flex flex-col items-center gap-4">
      <video ref={videoRef} src={videos.ai_summary_gif} playsInline autoPlay muted className="w-full h-full object-cover rounded-xl" />
      {/* Success Message */}
      <Text variant="small" className="text-ui_input_border_dark text-center leading-relaxed mx-auto">
        Thank you! Your consultation is submitted. The specialist will share the second opinion within 48 hours.
      </Text>

      {/* Proceed Button */}
      {/* <Button
        title="Proceed to Consultation page"
        onClick={handleProceedToConsultation}
        className="w-fit"
        isLoading={internalLoading}
        disabled={internalLoading}
      /> */}
    </div>
  );
};
